import type {
  AnimationBehavior,
  AnimationEffect,
  AnimationGroup,
  AnimationKeyframe,
  AnimationTarget,
  BehaviorTiming,
  EffectClass,
  EffectTrigger,
  InteractiveSequence,
  SlideTiming,
  TimingFill,
  Vector2,
} from '@slideforge/shared';
import type { Element } from '../xml.js';

/**
 * `p:timing` — M3 stage A.
 *
 * The tree is SMIL. A slide's main sequence is a list of click steps; each step
 * holds effects; each effect holds the behaviours that actually change
 * something. This parser keeps that shape rather than flattening it, because the
 * player needs the click steps as stop points and the behaviours as the only
 * trustworthy statement of what the author asked for.
 *
 * Nothing here throws. A timing tree that cannot be read leaves the slide static
 * and records why in `unsupported`, which is strictly better than refusing the
 * deck.
 */

const BEHAVIOR_NAMES = new Set([
  'p:set',
  'p:anim',
  'p:animEffect',
  'p:animScale',
  'p:animRot',
  'p:animClr',
  'p:animMotion',
  'p:cmd',
]);

const CONTAINER_NAMES = ['p:par', 'p:seq', 'p:excl'];
const CONTAINER_SET = new Set(CONTAINER_NAMES);

export function parseTiming(root: Element | undefined): SlideTiming | undefined {
  const timing = root?.child('p:timing') ?? root?.find((e) => e.name === 'p:timing');
  if (!timing) return undefined;

  const unsupported = new Set<string>();
  const sequences = topLevelSequences(timing);

  const groups: AnimationGroup[] = [];
  const interactive: InteractiveSequence[] = [];

  for (const seq of sequences) {
    const cTn = seq.child('p:cTn');
    if (cTn?.attr('nodeType') === 'interactiveSeq') {
      const parsed = parseInteractiveSequence(seq, unsupported);
      if (parsed) interactive.push(parsed);
      continue;
    }
    // Anything not explicitly interactive is treated as main-sequence content:
    // files written by other tools omit `nodeType` entirely.
    for (const groupEl of cTn?.child('p:childTnLst')?.anyOf(CONTAINER_NAMES) ?? []) {
      const group = parseGroup(groupEl, groups.length, unsupported);
      if (group) groups.push(group);
    }
  }

  if (timing.find((e) => e.name === 'p:excl')) unsupported.add('animation:timing:excl');

  const totalMs = groups.reduce((sum, g) => sum + g.delayMs + g.durationMs, 0);
  return { groups, interactive, totalMs, unsupported: [...unsupported].sort() };
}

/** How a `p:video` or `p:audio` media node says its shape should be played. */
export interface MediaPlayback {
  readonly autoPlay: boolean;
  readonly loop: boolean;
  /** 0..1. */
  readonly volume: number;
  readonly muted: boolean;
}

/**
 * Playback settings for every media shape on a slide, keyed by shape id.
 *
 * PowerPoint keeps a video's placement in the shape tree but its behaviour in
 * `p:timing`, under a `p:video`/`p:audio` node that targets the shape by id.
 * Reading it here rather than in the shape parser keeps the SMIL knowledge in
 * one file; `parseSlide` joins the two by id.
 *
 * A media node with no explicit start condition, or one that starts at a fixed
 * delay, plays on its own. `delay="indefinite"` means the learner has to click.
 */
export function parseMediaPlayback(root: Element | undefined): Map<string, MediaPlayback> {
  const out = new Map<string, MediaPlayback>();
  const timing = root?.child('p:timing') ?? root?.find((e) => e.name === 'p:timing');
  if (!timing) return out;

  for (const el of timing.walk()) {
    if (el.name !== 'p:video' && el.name !== 'p:audio') continue;
    const node = el.child('p:cMediaNode');
    if (!node) continue;

    const cTn = node.child('p:cTn');
    const delay = cTn?.path('p:stCondLst', 'p:cond')?.attr('delay');
    const playback: MediaPlayback = {
      autoPlay: delay !== 'indefinite',
      loop: cTn?.attr('repeatCount') === 'indefinite',
      // `vol` is a percentage in thousandths, so 80000 is 80%.
      volume: Math.max(0, Math.min(1, node.int('vol', 100000) / 100000)),
      muted: node.bool('mute', false),
    };

    for (const tgt of node.walk()) {
      if (tgt.name !== 'p:spTgt') continue;
      const spid = tgt.attr('spid');
      if (spid) out.set(spid, playback);
    }
  }
  return out;
}

/**
 * Every `p:seq` that is not already inside another one.
 *
 * Descent stops at a sequence because a nested sequence belongs to the effect
 * that contains it; collecting it twice would play the same effect twice.
 */
function topLevelSequences(timing: Element): Element[] {
  const out: Element[] = [];
  const visit = (el: Element): void => {
    for (const child of el.children) {
      if (child.name === 'p:seq') out.push(child);
      else visit(child);
    }
  };
  visit(timing);
  return out;
}

function parseInteractiveSequence(seq: Element, unsupported: Set<string>): InteractiveSequence | undefined {
  const cTn = seq.child('p:cTn');
  if (!cTn) return undefined;
  const cond = cTn.child('p:stCondLst')?.child('p:cond');
  const triggerShapeId = cond?.path('p:tgtEl', 'p:spTgt')?.attr('spid');
  if (!triggerShapeId) {
    unsupported.add('animation:trigger:non-shape');
    return undefined;
  }

  const drafts: EffectDraft[] = [];
  for (const child of cTn.child('p:childTnLst')?.anyOf(CONTAINER_NAMES) ?? []) {
    collectEffects(child, 0, drafts, unsupported);
  }
  const effects = resolveStartTimes(drafts);
  return {
    id: cTn.attr('id') ?? `interactive-${triggerShapeId}`,
    triggerShapeId,
    event: cond?.attr('evt') ?? 'onClick',
    durationMs: endOf(effects),
    effects,
  };
}

function parseGroup(groupEl: Element, index: number, unsupported: Set<string>): AnimationGroup | undefined {
  const cTn = groupEl.child('p:cTn');
  if (!cTn) return undefined;

  const drafts: EffectDraft[] = [];
  for (const child of cTn.child('p:childTnLst')?.anyOf(CONTAINER_NAMES) ?? []) {
    collectEffects(child, 0, drafts, unsupported);
  }
  // A group that is itself the effect — no wrapper level — still has to count.
  if (drafts.length === 0) collectEffects(groupEl, 0, drafts, unsupported);
  if (drafts.length === 0) return undefined;

  const effects = resolveStartTimes(drafts);
  const groupDelay = delayOf(cTn);
  // Click steps wait. A group whose own condition carries a real delay, or whose
  // first effect is not click-triggered, runs on from the previous step.
  const auto = groupDelay.kind === 'ms' || effects[0]?.trigger !== 'onClick';

  return {
    index,
    auto,
    delayMs: groupDelay.kind === 'ms' ? groupDelay.ms : 0,
    durationMs: endOf(effects),
    effects,
  };
}

type EffectDraft = Omit<AnimationEffect, 'startMs'>;

/**
 * Walks a time container, accumulating the delays of every enclosing node, and
 * emits one draft per effect node. Descent stops at an effect: everything below
 * it is that effect's own behaviours.
 */
function collectEffects(
  container: Element,
  inheritedDelay: number,
  out: EffectDraft[],
  unsupported: Set<string>,
): void {
  const cTn = container.child('p:cTn');
  if (!cTn) return;

  const delay = delayOf(cTn);
  const accumulated = inheritedDelay + (delay.kind === 'ms' ? delay.ms : 0);

  if (isEffectNode(cTn)) {
    out.push(effectFrom(cTn, accumulated, delay.kind === 'indefinite', unsupported));
    return;
  }

  for (const child of cTn.child('p:childTnLst')?.children ?? []) {
    if (CONTAINER_SET.has(child.name)) {
      collectEffects(child, accumulated, out, unsupported);
    } else if (BEHAVIOR_NAMES.has(child.name)) {
      // Behaviours sitting directly under a container: still animation, only
      // without the preset wrapper PowerPoint normally writes.
      out.push(effectFrom(cTn, accumulated, delay.kind === 'indefinite', unsupported));
      return;
    }
  }
}

function isEffectNode(cTn: Element): boolean {
  if (cTn.attr('presetClass') !== undefined) return true;
  const nodeType = cTn.attr('nodeType') ?? '';
  return nodeType === 'clickEffect' || nodeType === 'withEffect' || nodeType === 'afterEffect';
}

function effectFrom(
  cTn: Element,
  delayMs: number,
  waitsForClick: boolean,
  unsupported: Set<string>,
): EffectDraft {
  const behaviors: AnimationBehavior[] = [];
  for (const el of cTn.walk()) {
    if (!BEHAVIOR_NAMES.has(el.name)) {
      if (el.name === 'p:audio' || el.name === 'p:video') {
        unsupported.add(`animation:behavior:${el.name.slice(2)}`);
      }
      continue;
    }
    const behavior = parseBehavior(el, unsupported);
    if (behavior) behaviors.push(behavior);
  }

  const own = durationOf(cTn);
  const fromBehaviors = behaviors.reduce(
    (max, b) => Math.max(max, b.timing.delayMs + iterationSpan(b.timing)),
    0,
  );

  return {
    id: cTn.attr('id') ?? `effect-${delayMs}-${behaviors.length}`,
    presetClass: effectClassOf(cTn.attr('presetClass')),
    ...(cTn.attr('presetID') !== undefined ? { presetId: cTn.int('presetID', 0) } : {}),
    ...(cTn.attr('presetSubtype') !== undefined ? { presetSubtype: cTn.int('presetSubtype', 0) } : {}),
    trigger: triggerOf(cTn, waitsForClick),
    delayMs,
    durationMs: Math.max(own, fromBehaviors),
    target: behaviors.find((b) => b.timing.target.shapeId !== '')?.timing.target ?? { shapeId: '' },
    behaviors,
  };
}

/** One pass of a behaviour, ignoring an indefinite repeat so scheduling stays finite. */
function iterationSpan(timing: BehaviorTiming): number {
  const repeats = Number.isFinite(timing.repeatCount) ? Math.max(1, timing.repeatCount) : 1;
  return timing.durationMs * repeats;
}

function resolveStartTimes(drafts: readonly EffectDraft[]): AnimationEffect[] {
  const out: AnimationEffect[] = [];
  let previousStart = 0;
  let previousEnd = 0;

  drafts.forEach((draft, i) => {
    let base: number;
    if (i === 0) base = 0;
    else if (draft.trigger === 'withPrevious') base = previousStart;
    else base = previousEnd;

    const startMs = base + draft.delayMs;
    previousStart = startMs;
    previousEnd = startMs + draft.durationMs;
    out.push({ ...draft, startMs });
  });

  return out;
}

function endOf(effects: readonly AnimationEffect[]): number {
  return effects.reduce((max, e) => Math.max(max, e.startMs + e.durationMs), 0);
}

// --------------------------------------------------------------- behaviours

function parseBehavior(el: Element, unsupported: Set<string>): AnimationBehavior | undefined {
  const cBhvr = el.child('p:cBhvr');
  const timing = timingOf(cBhvr);

  switch (el.name) {
    case 'p:set': {
      const attribute = attributeNameOf(cBhvr);
      const to = valueOf(el.child('p:to'));
      if (attribute === undefined || to === undefined) return undefined;
      return { kind: 'set', timing, attribute, to };
    }
    case 'p:anim': {
      const attribute = attributeNameOf(cBhvr);
      if (attribute === undefined) return undefined;
      const from = el.attr('from') ?? valueOf(el.child('p:from'));
      const to = el.attr('to') ?? valueOf(el.child('p:to'));
      const by = el.attr('by') ?? valueOf(el.child('p:by'));
      return {
        kind: 'animate',
        timing,
        attribute,
        ...(from !== undefined ? { from } : {}),
        ...(to !== undefined ? { to } : {}),
        ...(by !== undefined ? { by } : {}),
        keyframes: keyframesOf(el.child('p:tavLst')),
        calcMode: calcModeOf(el.attr('calcmode') ?? el.attr('calcMode')),
      };
    }
    case 'p:animEffect': {
      const filter = el.attr('filter');
      if (filter === undefined || filter === '') return undefined;
      const transition = el.attr('transition');
      return {
        kind: 'filter',
        timing,
        filter,
        transition: transition === 'out' ? 'out' : transition === 'none' ? 'none' : 'in',
      };
    }
    case 'p:animScale': {
      const from = vectorOf(el.child('p:from'));
      const to = vectorOf(el.child('p:to'));
      const by = vectorOf(el.child('p:by'));
      return {
        kind: 'scale',
        timing,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(by ? { by } : {}),
        zoomContents: el.bool('zoomContents', false),
      };
    }
    case 'p:animRot': {
      const from = numberAttr(el, 'from');
      const to = numberAttr(el, 'to');
      const by = numberAttr(el, 'by');
      return {
        kind: 'rotate',
        timing,
        ...(from !== undefined ? { from } : {}),
        ...(to !== undefined ? { to } : {}),
        ...(by !== undefined ? { by } : {}),
      };
    }
    case 'p:animClr': {
      const to = colorOf(el.child('p:to'));
      const byHsl = hslOf(el.child('p:by'));
      if (to === undefined && byHsl === undefined) unsupported.add('animation:color:unresolved');
      return {
        kind: 'color',
        timing,
        colorSpace: el.attr('clrSpc') === 'hsl' ? 'hsl' : 'rgb',
        attribute: attributeNameOf(cBhvr) ?? 'style.color',
        ...(to !== undefined ? { to } : {}),
        ...(byHsl !== undefined ? { byHsl } : {}),
      };
    }
    case 'p:animMotion': {
      const path = el.attr('path');
      const by = vectorOf(el.child('p:by'));
      const from = vectorOf(el.child('p:from'));
      const to = vectorOf(el.child('p:to'));
      return {
        kind: 'motion',
        timing,
        ...(path ? { path } : {}),
        ...(by ? { by } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        origin: el.attr('origin') === 'parent' ? 'parent' : 'layout',
        rotateWithPath: el.attr('rAng') !== undefined && el.attr('rAng') !== '0',
      };
    }
    case 'p:cmd': {
      const type = el.attr('type');
      return {
        kind: 'command',
        timing,
        commandType: type === 'evt' ? 'evt' : type === 'verb' ? 'verb' : 'call',
        command: el.attr('cmd') ?? '',
      };
    }
    default:
      return undefined;
  }
}

function timingOf(cBhvr: Element | undefined): BehaviorTiming {
  const cTn = cBhvr?.child('p:cTn');
  const delay = cTn ? delayOf(cTn) : ({ kind: 'none' } as Delay);
  return {
    delayMs: delay.kind === 'ms' ? delay.ms : 0,
    durationMs: cTn ? durationOf(cTn) : 0,
    repeatCount: repeatCountOf(cTn),
    autoReverse: cTn?.bool('autoRev', false) ?? false,
    accelerate: fractionAttr(cTn, 'accel'),
    decelerate: fractionAttr(cTn, 'decel'),
    fill: fillOf(cTn?.attr('fill')),
    target: targetOf(cBhvr),
  };
}

function targetOf(cBhvr: Element | undefined): AnimationTarget {
  const spTgt = cBhvr?.path('p:tgtEl', 'p:spTgt');
  const pRg = spTgt?.path('p:txEl', 'p:pRg');
  return {
    shapeId: spTgt?.attr('spid') ?? '',
    ...(pRg ? { paragraphs: { start: pRg.int('st', 0), end: pRg.int('end', pRg.int('st', 0)) } } : {}),
    ...(spTgt?.child('p:bg') !== undefined ? { backgroundOnly: true } : {}),
  };
}

function attributeNameOf(cBhvr: Element | undefined): string | undefined {
  const name = cBhvr?.child('p:attrNameLst')?.child('p:attrName')?.innerText().trim();
  return name === undefined || name === '' ? undefined : name;
}

function keyframesOf(tavLst: Element | undefined): AnimationKeyframe[] {
  if (!tavLst) return [];
  const out: AnimationKeyframe[] = [];
  for (const tav of tavLst.all('p:tav')) {
    const raw = tav.attr('tm') ?? '0';
    // `tm` is thousandths of a percent of the behaviour's own duration.
    const time = raw === 'indefinite' ? 1 : Number.parseInt(raw, 10) / 100000;
    const value = valueOf(tav.child('p:val'));
    if (value === undefined || !Number.isFinite(time)) continue;
    out.push({ time: clamp01(time), value });
  }
  return out.sort((a, b) => a.time - b.time);
}

function valueOf(container: Element | undefined): string | undefined {
  if (!container) return undefined;
  for (const child of container.children) {
    switch (child.name) {
      case 'p:strVal':
      case 'p:fltVal':
      case 'p:intVal':
      case 'p:boolVal':
        return child.attr('val');
      default:
        break;
    }
  }
  return container.attr('val');
}

function vectorOf(el: Element | undefined): Vector2 | undefined {
  if (!el) return undefined;
  const x = numberAttr(el, 'x');
  const y = numberAttr(el, 'y');
  if (x === undefined && y === undefined) return undefined;
  return { x: x ?? 0, y: y ?? 0 };
}

function colorOf(el: Element | undefined): string | undefined {
  const srgb = el?.find((e) => e.name === 'a:srgbClr')?.attr('val');
  if (srgb) return srgb.toUpperCase();
  const rgb = el?.child('p:rgb');
  if (rgb) return `${hex(rgb.int('r', 0))}${hex(rgb.int('g', 0))}${hex(rgb.int('b', 0))}`;
  return undefined;
}

function hslOf(el: Element | undefined): { h: number; s: number; l: number } | undefined {
  const hsl = el?.child('p:hsl');
  if (!hsl) return undefined;
  return { h: hsl.int('h', 0), s: hsl.int('s', 0), l: hsl.int('l', 0) };
}

function hex(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0').toUpperCase();
}

// ------------------------------------------------------------------- atoms

type Delay = { kind: 'ms'; ms: number } | { kind: 'indefinite' } | { kind: 'none' };

function delayOf(cTn: Element): Delay {
  const cond = cTn.child('p:stCondLst')?.child('p:cond');
  const raw = cond?.attr('delay') ?? cTn.attr('delay');
  if (raw === undefined) return { kind: 'none' };
  if (raw === 'indefinite') return { kind: 'indefinite' };
  const ms = Number.parseInt(raw, 10);
  return Number.isFinite(ms) ? { kind: 'ms', ms } : { kind: 'none' };
}

function durationOf(cTn: Element): number {
  const raw = cTn.attr('dur');
  if (raw === undefined || raw === 'indefinite') return 0;
  const ms = Number.parseInt(raw, 10);
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
}

function repeatCountOf(cTn: Element | undefined): number {
  const raw = cTn?.attr('repeatCount');
  if (raw === undefined) return 1;
  if (raw === 'indefinite') return Number.POSITIVE_INFINITY;
  // Thousandths: `repeatCount="3000"` is three passes.
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n / 1000 : 1;
}

function fractionAttr(cTn: Element | undefined, name: string): number {
  if (!cTn) return 0;
  const raw = cTn.attr(name);
  if (raw === undefined) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? clamp01(n / 100000) : 0;
}

function numberAttr(el: Element, name: string): number | undefined {
  const raw = el.attr(name);
  if (raw === undefined) return undefined;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}

function fillOf(raw: string | undefined): TimingFill {
  switch (raw) {
    case 'freeze':
      return 'freeze';
    case 'remove':
      return 'remove';
    case 'transition':
      return 'transition';
    default:
      return 'hold';
  }
}

function effectClassOf(raw: string | undefined): EffectClass {
  switch (raw) {
    case 'entr':
      return 'entrance';
    case 'exit':
      return 'exit';
    case 'emph':
      return 'emphasis';
    case 'path':
      return 'motionPath';
    case 'mediacall':
      return 'mediaCall';
    case 'verb':
      return 'verb';
    default:
      return 'other';
  }
}

function triggerOf(cTn: Element, waitsForClick: boolean): EffectTrigger {
  switch (cTn.attr('nodeType')) {
    case 'clickEffect':
    case 'onClick':
      return 'onClick';
    case 'withEffect':
      return 'withPrevious';
    case 'afterEffect':
      return 'afterPrevious';
    default:
      return waitsForClick ? 'onClick' : 'withPrevious';
  }
}

function calcModeOf(raw: string | undefined): 'discrete' | 'linear' | 'formula' {
  switch (raw) {
    case 'discrete':
      return 'discrete';
    case 'fmla':
      return 'formula';
    default:
      return 'linear';
  }
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}
